import { useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import type { CheckoutFormValues } from "@/domain/checkout";
import { useIntl } from "@/infra/locale";
import { webshopEnvironment } from "@akina/config";
import { Button, DateOfBirthInput, ErrorMessage, Headline, Icon, InputWithError } from "@akina/presentation";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";

function buildUrl(base: string, path: string): string {
  try {
    return new URL(path, base).href;
  } catch {
    return "#";
  }
}

const DOB_REGEX = /^\d{2}\.\d{2}\.\d{4}$/;
const NAME_REGEX = /^\p{L}[\p{L} '-]*$/u;
const MIN_AGE = 18;
const MIN_DOB_YEAR = 1900;

function parseDDMMYYYY(value: string): Date | null {
  const [day, month, year] = value.split(".").map(Number);
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return null;
  return date;
}

function buildSchema(errors: {
  firstName: string;
  lastName: string;
  dobInvalid: string;
  dobTooRecent: string;
  email: string;
}) {
  return z.object({
    firstName: z.string().min(1, errors.firstName).regex(NAME_REGEX, errors.firstName),
    lastName: z.string().min(1, errors.lastName).regex(NAME_REGEX, errors.lastName),
    dateOfBirth: z
      .string()
      .regex(DOB_REGEX, errors.dobInvalid)
      .refine(val => parseDDMMYYYY(val) !== null, { message: errors.dobInvalid })
      .refine(
        val => {
          const d = parseDDMMYYYY(val);
          return d ? d.getFullYear() >= MIN_DOB_YEAR : true;
        },
        { message: errors.dobInvalid },
      )
      .refine(
        val => {
          const d = parseDDMMYYYY(val);
          return d ? d <= new Date() : true;
        },
        { message: errors.dobInvalid },
      )
      .refine(
        val => {
          const d = parseDDMMYYYY(val);
          if (!d) return true;
          const today = new Date();
          const cutoff = new Date(today.getFullYear() - MIN_AGE, today.getMonth(), today.getDate());
          return d <= cutoff;
        },
        { message: errors.dobTooRecent },
      ),
    email: z.string().email(errors.email),
  });
}

type PersonalDetailsFormProps = {
  onSubmit: (values: CheckoutFormValues) => void;
  isPending?: boolean;
  isError?: boolean;
  isEmailConflict?: boolean;
  onEmailChange?: () => void;
  defaultValues?: Partial<CheckoutFormValues>;
};

export function PersonalDetailsForm({ onSubmit, isPending = false, isError = false, isEmailConflict = false, onEmailChange, defaultValues }: Readonly<PersonalDetailsFormProps>) {
  const { formatMessage } = useIntl();

  const schema = useMemo(
    () =>
      buildSchema({
        firstName: formatMessage({ id: "checkoutPage.paymentStep.personalDetailsForm.firstNameError" }),
        lastName: formatMessage({ id: "checkoutPage.paymentStep.personalDetailsForm.lastNameError" }),
        dobInvalid: formatMessage({ id: "checkoutPage.paymentStep.personalDetailsForm.dateOfBirthInvalid" }),
        dobTooRecent: formatMessage({ id: "checkoutPage.paymentStep.personalDetailsForm.dateOfBirthTooRecent" }),
        email: formatMessage({ id: "checkoutPage.paymentStep.personalDetailsForm.emailInvalid" }),
      }),
    [formatMessage],
  );

  const { control, handleSubmit, formState, getValues, trigger } = useForm<CheckoutFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { firstName: "", lastName: "", dateOfBirth: "", email: "", ...defaultValues },
    mode: "onTouched",
  });

  useEffect(() => {
    const values = getValues();
    const hasPrefilledValues = Object.values(values).some(value => value.trim() !== "");
    if (hasPrefilledValues) trigger();
  }, [getValues, trigger]);

  return (
    <section className="flex flex-1 flex-col gap-8 rounded-3xl bg-white px-6 py-9">
      <div className="border-b border-gray-800/10 pb-4">
        <Headline size="xl" fontWeight="font-bold" color="text-gray-800">
          {formatMessage({ id: "checkoutPage.paymentStep.personalDetailsForm.title" })}
        </Headline>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} noValidate className="flex flex-col gap-8">
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-4 md:flex-row">
            <Controller
              name="firstName"
              control={control}
              render={({ field, fieldState }) => (
                <InputWithError
                  wrapperClassName="flex-1"
                  label={formatMessage({ id: "checkoutPage.paymentStep.personalDetailsForm.firstNameLabel" })}
                  labelPosition="top"
                  placeholder={formatMessage({
                    id: "checkoutPage.paymentStep.personalDetailsForm.firstNamePlaceholder",
                  })}
                  error={fieldState.error?.message}
                  autoComplete="given-name"
                  fullWidth
                  {...field}
                />
              )}
            />
            <Controller
              name="lastName"
              control={control}
              render={({ field, fieldState }) => (
                <InputWithError
                  wrapperClassName="flex-1"
                  label={formatMessage({ id: "checkoutPage.paymentStep.personalDetailsForm.lastNameLabel" })}
                  labelPosition="top"
                  placeholder={formatMessage({
                    id: "checkoutPage.paymentStep.personalDetailsForm.lastNamePlaceholder",
                  })}
                  error={fieldState.error?.message}
                  autoComplete="family-name"
                  fullWidth
                  {...field}
                />
              )}
            />
            <Controller
              name="dateOfBirth"
              control={control}
              render={({ field, fieldState }) => (
                <div className="flex flex-col md:w-[200px] md:shrink-0">
                  <DateOfBirthInput
                    name={field.name}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    label={formatMessage({ id: "checkoutPage.paymentStep.personalDetailsForm.dateOfBirthLabel" })}
                    placeholder={formatMessage({
                      id: "checkoutPage.paymentStep.personalDetailsForm.dateOfBirthPlaceholder",
                    })}
                    invalid={!!fieldState.error}
                  />
                  <ErrorMessage error={fieldState.error?.message} size="xs" className="mt-2" />
                </div>
              )}
            />
          </div>

          <Controller
            name="email"
            control={control}
            render={({ field, fieldState }) => (
              <InputWithError
                label={formatMessage({ id: "checkoutPage.paymentStep.personalDetailsForm.emailLabel" })}
                labelPosition="top"
                placeholder={formatMessage({ id: "checkoutPage.paymentStep.personalDetailsForm.emailPlaceholder" })}
                type="email"
                error={isEmailConflict
                  ? formatMessage({ id: "checkoutPage.paymentStep.personalDetailsForm.emailAlreadyUsed" })
                  : fieldState.error?.message}
                autoComplete="email"
                {...field}
                onChange={e => {
                  onEmailChange?.();
                  field.onChange(e);
                }}
              />
            )}
          />
        </div>

        <p className="font-area text-sm text-black">
          {formatMessage(
            { id: "checkoutPage.paymentStep.personalDetailsForm.termsAgreementStatement" },
            {
              termsLabel: (
                <a key="terms" href={buildUrl(webshopEnvironment.akinaCareUrl, "terms-and-conditions")} target="_blank" rel="noreferrer" className="text-primary-100">
                  {formatMessage({ id: "checkoutPage.paymentStep.personalDetailsForm.termsAndConditionsLabel" })}
                </a>
              ),
              privacyPolicyLabel: (
                <a key="privacy" href={buildUrl(webshopEnvironment.akinaCareUrl, "privacy-policy-and-data-transparency")} target="_blank" rel="noreferrer" className="text-primary-100">
                  {formatMessage({ id: "checkoutPage.paymentStep.personalDetailsForm.privacyPolicyLabel" })}
                </a>
              ),
            },
          )}
        </p>

     {isError ? (
        <div role="alert" aria-live="assertive" aria-atomic="true" className="bg-info-50 flex items-center gap-3 rounded-2xl px-4 py-3">
          <Icon type="infoOutlined" className="text-info-900 shrink-0" />
          <p className="font-area text-info-900 text-base">
            {formatMessage({ id: "checkoutPage.paymentStep.personalDetailsForm.paymentFailedText" })}
          </p>
        </div>
      ): null}

       

        <div className="flex justify-end">
          <Button type="submit" size="medium" className="w-auto" disabled={!formState.isValid || isEmailConflict || isPending} loading={isPending}>
            {formatMessage({ id: "checkoutPage.paymentStep.personalDetailsForm.continueToPaymentButton" })}
          </Button>
        </div>
      </form>
    </section>
  );
}
